import { Input } from '../../ui/input';
import { Textarea } from '../../ui/textarea';
import { Checkbox } from '../../ui/checkbox';
import { RadioGroup, RadioGroupItem } from '../../ui/radio-group';
import { Label } from '../../ui/label';
import FieldError from '../FieldError';

const YES_NO = [
  { v: 'No', id: 'police-no' },
  { v: 'Yes', id: 'police-yes' },
];
const CITATION = [
  { v: 'No', id: 'citation-no' },
  { v: 'Yes', id: 'citation-yes' },
];
const VEHICLE = [
  { v: 'CMV', id: 'cmv', label: 'CMV' },
  { v: 'NA', id: 'na', label: 'NA' },
  { v: 'Non-CMV', id: 'non-cmv', label: 'Non-CMV' },
];

/** Step 6 — Location, description, police involvement, other-vehicle classifier
 *  and the tow/injury/fatality checkbox row. The most field-heavy step. */
export default function LocationStep({
  formData, errors, handleChange,
  setFormData, setErrors, handleCheckboxChange,
}) {
  return (
    <div>
      <label className="text-blue-500 text-2xl font-bold mb-6 block">Location and Details</label>
      <div className="space-y-6">
        <div>
          <Input
            type="text"
            placeholder="Enter Location [City, Street, State] or [Hwy, Direction, Mm, State]"
            value={formData.location}
            onChange={(e) => handleChange('location', e.target.value)}
            className={`w-full bg-white h-12 text-base placeholder:text-gray-400 placeholder:italic ${
              errors.location ? 'border-red-500' : ''
            }`}
          />
          <FieldError message={errors.location} className="mt-1" />
        </div>
        <div>
          <Textarea
            placeholder="Describe Loss"
            value={formData.lossDescription}
            onChange={(e) => handleChange('lossDescription', e.target.value)}
            rows={8}
            className={`w-full bg-white text-base placeholder:text-gray-400 placeholder:italic resize-none ${
              errors.lossDescription ? 'border-red-500' : ''
            }`}
          />
          <FieldError message={errors.lossDescription} className="mt-1" />
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 sm:gap-8 pt-4">
          <div>
            <Label className="text-lg text-gray-500 mb-3 block">Police Involved</Label>
            <RadioGroup
              value={formData.policeInvolved}
              onValueChange={(value) => {
                handleChange('policeInvolved', value);
                if (value === 'No') {
                  setFormData((prev) => ({
                    ...prev,
                    citationIssued: '',
                    policeReportNo: '',
                    policeReportNotReady: false,
                  }));
                  setErrors((prev) => ({ ...prev, citationIssued: '', policeReportNo: '' }));
                }
              }}
            >
              <div className="flex gap-6">
                {YES_NO.map((o) => (
                  <div key={o.id} className="flex items-center space-x-2">
                    <RadioGroupItem value={o.v} id={o.id} className="w-5 h-5" />
                    <Label htmlFor={o.id} className="text-lg text-gray-600 cursor-pointer">{o.v}</Label>
                  </div>
                ))}
              </div>
            </RadioGroup>
            <FieldError message={errors.policeInvolved} />
          </div>

          {formData.policeInvolved === 'Yes' && (
            <div>
              <Label className="text-lg text-gray-500 mb-3 block">Citation Issued</Label>
              <RadioGroup value={formData.citationIssued} onValueChange={(v) => handleChange('citationIssued', v)}>
                <div className="flex gap-6">
                  {CITATION.map((o) => (
                    <div key={o.id} className="flex items-center space-x-2">
                      <RadioGroupItem value={o.v} id={o.id} className="w-5 h-5" />
                      <Label htmlFor={o.id} className="text-lg text-gray-600 cursor-pointer">{o.v}</Label>
                    </div>
                  ))}
                </div>
              </RadioGroup>
              <FieldError message={errors.citationIssued} />
            </div>
          )}

          <div>
            <Label className="text-lg text-gray-500 mb-3 block whitespace-nowrap">Other Vehicle Involved</Label>
            <RadioGroup value={formData.otherVehicle} onValueChange={(v) => handleChange('otherVehicle', v)}>
              <div className="flex items-center gap-6">
                {VEHICLE.map((o) => (
                  <div key={o.id} className="flex items-center space-x-2">
                    <RadioGroupItem value={o.v} id={o.id} className="w-5 h-5" />
                    <Label htmlFor={o.id} className={`text-lg text-gray-600 cursor-pointer ${o.v === 'Non-CMV' ? 'whitespace-nowrap' : ''}`}>
                      {o.label}
                    </Label>
                  </div>
                ))}
              </div>
            </RadioGroup>
            <FieldError message={errors.otherVehicle} />
          </div>
        </div>

        {formData.policeInvolved === 'Yes' && (
          <div className="pt-4">
            <Label className="text-lg text-gray-500 mb-2 block">Police report</Label>
            <div className="flex items-center gap-3">
              <Input
                type="text"
                placeholder="Enter Report #"
                value={formData.policeReportNo}
                onChange={(e) => {
                  handleChange('policeReportNo', e.target.value);
                  if (e.target.value.trim()) {
                    setFormData((prev) => ({ ...prev, policeReportNotReady: false }));
                  }
                }}
                disabled={formData.policeReportNotReady}
                className={`w-64 bg-white h-10 text-base placeholder:text-gray-400 placeholder:italic ${
                  formData.policeReportNotReady ? 'opacity-50' : ''
                } ${errors.policeReportNo ? 'border-red-500' : ''}`}
              />
              {!formData.policeReportNo?.trim() && (
                <div className="flex items-center space-x-1.5">
                  <Checkbox
                    id="policeReportNotReady"
                    checked={formData.policeReportNotReady}
                    onCheckedChange={(checked) => {
                      setFormData((prev) => ({ ...prev, policeReportNotReady: checked }));
                      if (checked) setErrors((prev) => ({ ...prev, policeReportNo: '' }));
                    }}
                    className="w-5 h-5 border-2"
                    data-testid="police-report-not-ready"
                  />
                  <Label htmlFor="policeReportNotReady" className="text-sm text-gray-600 cursor-pointer whitespace-nowrap">
                    Not ready
                  </Label>
                </div>
              )}
            </div>
            <FieldError message={errors.policeReportNo} className="mt-1" />
          </div>
        )}

        <div className="flex flex-wrap gap-6 md:gap-12 pt-6">
          {[
            { field: 'towAway', label: 'Tow Away' },
            { field: 'injury', label: 'Injury' },
            { field: 'fatality', label: 'Fatality' },
          ].map(({ field, label }) => (
            <div key={field} className="flex items-center space-x-3">
              <Checkbox
                id={field}
                checked={formData[field]}
                onCheckedChange={(checked) => handleCheckboxChange(field, checked)}
                className="w-6 h-6 border-2"
              />
              <Label htmlFor={field} className="text-xl text-gray-600 cursor-pointer">{label}</Label>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
